import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchBoardAppealListAPI } from "../service/boardAppeals";

/**
 * Only what the home tile needs: whether this user may open the Board
 * appeal pile, how many are waiting, and the nearest deadline. The page
 * keeps its own state.
 */

const URGENT_DAYS = 30;

// The pending list arrives soonest deadline first.
const summarize = (items = []) => {
  const dated = items.filter((r) => typeof r.daysLeft === "number");
  const next = dated[0] || null;
  return {
    urgentCount: dated.filter((r) => r.daysLeft <= URGENT_DAYS).length,
    nextDue: next
      ? {
          clientName: next.clientName || "",
          deadline: next.deadline || null,
          daysLeft: next.daysLeft,
        }
      : null,
  };
};

export const fetchBoardAppealAvailability = createAsyncThunk(
  "boardAppeals/fetchAvailability",
  async () => {
    try {
      const response = await fetchBoardAppealListAPI("pending");
      return {
        available: true,
        count: response?.data?.counts?.pending ?? 0,
        ...summarize(response?.data?.items),
      };
    } catch (error) {
      // Hidden when the routes are not deployed or the user is not a
      // reviewer, so nobody sees a door they cannot open.
      return { available: false, count: 0, urgentCount: 0, nextDue: null };
    }
  }
);

const BoardAppealSlice = createSlice({
  name: "boardAppeals",
  initialState: {
    available: false,
    loaded: false,
    pendingCount: 0,
    urgentCount: 0,
    nextDue: null,
  },
  reducers: {
    setBoardAppealPendingCount: (state, action) => {
      state.available = true;
      state.pendingCount = action.payload ?? 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBoardAppealAvailability.fulfilled, (state, action) => {
      state.available = action.payload.available;
      state.loaded = true;
      state.pendingCount = action.payload.count;
      state.urgentCount = action.payload.urgentCount;
      state.nextDue = action.payload.nextDue;
    });
  },
});

export const { setBoardAppealPendingCount } = BoardAppealSlice.actions;

export const selectBoardAppealAvailable = (state) =>
  state.boardAppeals?.available;
export const selectBoardAppealPendingCount = (state) =>
  state.boardAppeals?.pendingCount ?? 0;
export const selectBoardAppealSummary = (state) => state.boardAppeals;

export default BoardAppealSlice.reducer;
